/**
 * Frontend scope permission helpers.
 *
 * SCOPE_ALIAS_MAP: maps each specific scope to parent/legacy scopes that also grant it.
 * This mirrors the backend's RequireOauthScope OR logic and supports legacy manage scopes.
 *
 * When adding a new module:
 *   1. Add scope_id entries to SCOPE_ALIAS_MAP for each action
 *   2. Include the .manage alias and system:users-management as fallbacks
 */

const SCOPE_ALIAS_MAP = {
  'dashboard.view': ['system:users-management'],

  'oauth-users.view':               ['oauth-users.manage', 'system:users-management'],
  'oauth-users.create':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.update':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.delete':             ['oauth-users.manage', 'system:users-management'],
  'oauth-users.permissions.view':   ['oauth-users.manage', 'system:users-management', 'system:scope-management'],
  'oauth-users.permissions.update': ['oauth-users.manage', 'system:users-management', 'system:scope-management'],

  'oauth-clients.view':          ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.create':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.update':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.delete':        ['oauth-clients.manage', 'system:users-management'],
  'oauth-clients.rotate-secret': ['oauth-clients.manage', 'system:users-management'],

  'upload-s3-public-share-token.view':       ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.update':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.regenerate': ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.toggle':     ['upload-s3-public-share-token.manage', 'system:users-management'],
  'upload-s3-public-share-token.export':     ['upload-s3-public-share-token.manage', 'system:users-management'],

  'example-batch-upload.view':   ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.create': ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.update': ['example-batch-upload.manage', 'system:users-management'],
  'example-batch-upload.delete': ['example-batch-upload.manage', 'system:users-management'],

  'chat.view':         ['chat.manage', 'system:users-management'],
  'chat.create':       ['chat.manage', 'system:users-management'],
  'chat.send':         ['chat.manage', 'system:users-management'],
  'chat.manage-group': ['chat.manage', 'system:users-management'],

  // Voice + Video Calls (Architecture Rule 74)
  'calls.view':                ['calls.manage', 'system:users-management'],
  'calls.create':              ['calls.manage', 'system:users-management'],
  'calls.join':                ['calls.create', 'calls.manage', 'system:users-management'],
  'calls.end':                 ['calls.manage', 'system:users-management'],
  'calls.recordings.view':     ['calls.manage', 'system:users-management'],
  'calls.recordings.download': ['calls.recordings.view', 'calls.manage', 'system:users-management'],

  // ── CRM core (Rules 65 / 75 / 76) ───────────────────────────────────────────
  // The three visibility tiers are ADDITIVE: everyone who can read holds `.view`,
  // and `.view-team` / `.view-all` only WIDEN which rows come back. So `.view` is
  // the scope a nav item or a read button checks, and the wider tiers are listed
  // as aliases — a manager holding only `.view-all` still passes a `.view` gate.
  'organizations.view':      ['organizations.view-team', 'organizations.view-all', 'organizations.manage', 'system:users-management'],
  'organizations.view-team': ['organizations.view-all', 'organizations.manage', 'system:users-management'],
  'organizations.view-all':  ['organizations.manage', 'system:users-management'],
  'organizations.create':    ['organizations.manage', 'system:users-management'],
  'organizations.update':    ['organizations.manage', 'system:users-management'],
  'organizations.delete':    ['organizations.manage', 'system:users-management'],
  'organizations.merge':     ['organizations.manage', 'system:users-management'],
  'organizations.export':    ['organizations.manage', 'system:users-management'],

  'contacts.view':      ['contacts.view-team', 'contacts.view-all', 'contacts.manage', 'system:users-management'],
  'contacts.view-team': ['contacts.view-all', 'contacts.manage', 'system:users-management'],
  'contacts.view-all':  ['contacts.manage', 'system:users-management'],
  'contacts.create':    ['contacts.manage', 'system:users-management'],
  'contacts.update':    ['contacts.manage', 'system:users-management'],
  'contacts.delete':    ['contacts.manage', 'system:users-management'],
  'contacts.merge':     ['contacts.manage', 'system:users-management'],
  'contacts.export':    ['contacts.manage', 'system:users-management'],

  'activities.view':      ['activities.view-team', 'activities.view-all', 'activities.manage', 'system:users-management'],
  'activities.view-team': ['activities.view-all', 'activities.manage', 'system:users-management'],
  'activities.view-all':  ['activities.manage', 'system:users-management'],
  'activities.create':    ['activities.manage', 'system:users-management'],
  'activities.update':    ['activities.manage', 'system:users-management'],
  'activities.delete':    ['activities.manage', 'system:users-management'],
  'activities.complete':  ['activities.manage', 'system:users-management'],

  // Rule 77 — the AGENT side of the website widget. The visitor side is public
  // and carries no scope at all, so there is deliberately nothing here for it.
  'livechat.view':     ['livechat.manage', 'system:users-management'],
  'livechat.answer':   ['livechat.manage', 'system:users-management'],
  'livechat.transfer': ['livechat.manage', 'system:users-management'],
  'livechat.close':    ['livechat.manage', 'system:users-management'],

  'leads.view':      ['leads.view-team', 'leads.view-all', 'leads.manage', 'system:users-management'],
  'leads.view-team': ['leads.view-all', 'leads.manage', 'system:users-management'],
  'leads.view-all':  ['leads.manage', 'system:users-management'],
  'leads.create':    ['leads.manage', 'system:users-management'],
  'leads.update':    ['leads.manage', 'system:users-management'],
  'leads.delete':    ['leads.manage', 'system:users-management'],
  'leads.assign':    ['leads.manage', 'system:users-management'],
  'leads.convert':   ['leads.manage', 'system:users-management'],
  'leads.export':    ['leads.manage', 'system:users-management'],

  'opportunities.view':       ['opportunities.view-team', 'opportunities.view-all', 'opportunities.manage', 'system:users-management'],
  'opportunities.view-team':  ['opportunities.view-all', 'opportunities.manage', 'system:users-management'],
  'opportunities.view-all':   ['opportunities.manage', 'system:users-management'],
  'opportunities.create':     ['opportunities.manage', 'system:users-management'],
  'opportunities.update':     ['opportunities.manage', 'system:users-management'],
  'opportunities.delete':     ['opportunities.manage', 'system:users-management'],
  'opportunities.assign':     ['opportunities.manage', 'system:users-management'],
  'opportunities.move-stage': ['opportunities.manage', 'system:users-management'],
  // Reopening a closed deal rewrites a reported number — manager-only, so it
  // deliberately does NOT inherit from move-stage.
  'opportunities.reopen':     ['opportunities.manage', 'system:users-management'],
  'opportunities.export':     ['opportunities.manage', 'system:users-management'],

  'pipelines.view':   ['pipelines.manage', 'opportunities.view', 'system:users-management'],
  'pipelines.create': ['pipelines.manage', 'system:users-management'],
  'pipelines.update': ['pipelines.manage', 'system:users-management'],
  'pipelines.delete': ['pipelines.manage', 'system:users-management'],

  'sales-teams.view':   ['sales-teams.manage', 'system:users-management'],
  'sales-teams.create': ['sales-teams.manage', 'system:users-management'],
  'sales-teams.update': ['sales-teams.manage', 'system:users-management'],
  'sales-teams.delete': ['sales-teams.manage', 'system:users-management'],
  'sales-teams.manage': ['system:users-management'],

  'tags.view':   ['tags.manage', 'system:users-management'],
  'tags.create': ['tags.manage', 'system:users-management'],
  'tags.update': ['tags.manage', 'system:users-management'],
  'tags.delete': ['tags.manage', 'system:users-management'],

  'custom-fields.view': ['custom-fields.manage', 'system:users-management'],

  'notifications.view': ['notifications.manage', 'system:users-management'],

  // Authorized Devices (Rule 73)
  'device-registration.view':   ['device-registration.delete', 'system:users-management'],
  'device-registration.delete': ['system:users-management'],

  // System Settings (Rule 74 — Phase 1 settings stack)
  'system-settings.view':   ['system-settings.manage', 'system:users-management'],
  'system-settings.update': ['system-settings.manage', 'system:users-management'],

  // System Administrator read-only viewers (Rule 68; Rule 65 system:* exception)
  'admin-audit-logs.view':          ['system:users-management'],
  'admin-audit-logs.export':        ['system:users-management'],
  'admin-outbound-http-logs.view':  ['system:users-management'],
  'admin-field-history.view':       ['system:users-management'],
  'admin-login-attempts.view':      ['system:users-management'],
  'admin-chat-messages.view':       ['system:users-management'],
  'admin-webhook-deliveries.view':  ['system:users-management'],
  'admin-failed-jobs.view':         ['system:users-management'],
  'admin-failed-jobs.retry':        ['system:users-management'],

  // ── Add new module scopes here ─────────────────────────────────────────────
  // Pattern:
  // '{module-key}.view':   ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.create': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.update': ['{module-key}.manage', 'system:users-management'],
  // '{module-key}.delete': ['{module-key}.manage', 'system:users-management'],
};

/**
 * Returns all scope IDs that grant a given required scope
 * (the scope itself plus all its aliases).
 */
function grantedScopesFor(requiredScope) {
  return [requiredScope, ...(SCOPE_ALIAS_MAP[requiredScope] ?? [])];
}

/**
 * Normalize a raw scope value from the API to a plain string array.
 */
export function normalizeScopeList(scopes) {
  return Array.isArray(scopes) ? scopes : [];
}

/**
 * Returns true if userScopes satisfies requiredScope (including aliases).
 */
export function hasScope(userScopes, requiredScope) {
  return grantedScopesFor(requiredScope).some((s) => userScopes.includes(s));
}

/**
 * Returns true if userScopes satisfies ANY scope in requiredScopes (OR logic).
 */
export function hasAnyScope(userScopes, requiredScopes) {
  return requiredScopes.some((s) => hasScope(userScopes, s));
}
